/**
 * Settings Action Creators
 * Contains functions for managing application settings such as theme preferences
 * @module Settings/Actions
 */
import { store } from '../../../redux/store';
import { setDark } from './reducer';
import { getLocalData, setLocalData } from '../../../utils/storage';
import { keys } from '../../../api/keys';
import { flushSync } from 'react-dom';

/**
 * Toggles the dark mode theme and applies a transition animation if supported.
 *
 * The function handles three main cases:
 * 1. Initialization - Retrieves saved dark mode preference
 * 2. Toggle with animation - Changes theme with a smooth circular transition
 * 3. Toggle without animation - Changes theme instantly
 *
 * Note: Ensure that the following CSS is added to your global stylesheet
 * to properly handle view transitions:
 *
 * ```css
 * ::view-transition-old(root),
 * ::view-transition-new(root) {
 *   animation: none;
 *   mix-blend-mode: normal;
 * }
 * ```
 *
 * @param {Object} params - The parameters object.
 * @param {boolean} [params.isDarkMode] - Flag indicating if dark mode is currently active.
 * @param {React.RefObject} [params.switchThumbRef] - Reference to the thumb element of the switch toggle, used for animation calculations.
 * @param {boolean} [params.isInitialize] - Flag indicating if the function is being called for initialization, to retrieve saved dark mode state.
 *
 * @returns {Promise<void>} Toggles the dark mode and triggers a view transition if applicable.
 */
export const toggleDarkMode = async ({
  isDarkMode,
  switchThumbRef,
  isInitialize,
}: {
  isDarkMode?: boolean;
  switchThumbRef?: any;
  isInitialize?: boolean;
}): Promise<void> => {
  let transition = null;

  // For non-initialization toggle, schedule any heavy operations to happen after the first frame
  // This helps improve perceived performance during theme switching
  const isToggleAction = !isInitialize && isDarkMode !== undefined;

  try {
    // Determine the dark mode state: if initializing, fetch from local storage
    let tempIsDarkMode = isDarkMode;

    if (isInitialize) {
      // When initializing, try to get from storage
      const storedValue = await getLocalData(keys.DARK_MODE);

      // Only use stored value if it's not null and is boolean
      if (storedValue !== null && typeof storedValue === 'boolean') {
        tempIsDarkMode = storedValue;
      } else {
        // If no valid stored value, use the current state
        tempIsDarkMode = store.getState().settings.isDarkMode;

        // Save the current state to localStorage to fix any potential issues
        await setLocalData(keys.DARK_MODE, tempIsDarkMode);
      }
    } else {
      // For toggle actions
      if (isDarkMode === undefined) {
        // If no explicit value provided, toggle current state
        tempIsDarkMode = !store.getState().settings.isDarkMode;
      }

      // Update the Redux store immediately for a snappy UI response
      store.dispatch(setDark(tempIsDarkMode));

      // Defer storage operations to the next animation frame to prioritize UI responsiveness
      if (isToggleAction) {
        requestAnimationFrame(async () => {
          try {
            await setLocalData(keys.DARK_MODE, tempIsDarkMode);
            // Verify the value was stored correctly
            const storedValue = await getLocalData(keys.DARK_MODE);

            if (storedValue !== tempIsDarkMode) {
              // Try with direct localStorage as a fallback
              localStorage.setItem('DIRECT_DARK_MODE', tempIsDarkMode.toString());
            }
          } catch (storageError) {
            console.error('Error storing dark mode preference:', storageError);
          }
        });

        // For non-animated toggle, we can exit early since we already updated the Redux store
        if (
          !switchThumbRef?.current?.getBoundingClientRect()?.top ||
          !document?.startViewTransition ||
          window.matchMedia('(prefers-reduced-motion: reduce)').matches
        ) {
          return;
        }
      } else {
        // Regular flow for non-deferred operations
        try {
          await setLocalData(keys.DARK_MODE, tempIsDarkMode);
          // Verify the value was stored correctly
          const storedValue = await getLocalData(keys.DARK_MODE);
          console.log('Verified stored value after toggle:', storedValue);

          if (storedValue !== tempIsDarkMode) {
            console.error('Storage inconsistency detected, stored value does not match expected value');
            // Try with direct localStorage as a fallback
            localStorage.setItem('DIRECT_DARK_MODE', tempIsDarkMode.toString());
          }
        } catch (storageError) {
          console.error('Error storing dark mode preference:', storageError);
        }
      }
    }

    // Before dispatching to Redux, make sure we have a valid boolean value
    if (typeof tempIsDarkMode !== 'boolean') {
      console.log('Non-boolean value for dark mode, defaulting to false');
      tempIsDarkMode = false;
    }

    /**
     * Exit early if:
     * 1. The switch thumb reference is not available,
     * 2. The View Transition API is not supported, or
     * 3. The user prefers reduced motion, which disables animations.
     */
    if (
      !switchThumbRef?.current?.getBoundingClientRect()?.top ||
      !document?.startViewTransition ||
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    ) {
      // Skip this dispatch if we've already done it for optimized toggle path
      if (!isToggleAction) {
        store.dispatch(setDark(tempIsDarkMode));
      }
      return;
    }
    // Avoid redundant dispatch for optimized toggle path
    if (!isToggleAction) {
      // Trigger a view transition animation for toggling dark mode
      transition = await document.startViewTransition(() => {
        flushSync(() => {
          store.dispatch(setDark(tempIsDarkMode));
        });
      }).ready;
    } else {
      // For optimized toggle path, just start the transition without another dispatch
      transition = await document.startViewTransition(() => {
        // Nothing to dispatch, state is already updated
      }).ready;
    }

    // Get the position and dimensions of the switch thumb for animation purposes
    const { top, left, width, height } = switchThumbRef?.current?.getBoundingClientRect() || {};
    const x = left + width / 2;
    const y = top + height / 2;
    const right = window.innerWidth - left;
    const bottom = window.innerHeight - top;
    const maxRadius = Math.hypot(Math.max(left, right), Math.max(top, bottom));

    // Apply a circular clip path animation to smoothly transition between light and dark mode
    document.documentElement.animate(
      {
        clipPath: [`circle(0px at ${x}px ${y}px)`, `circle(${maxRadius}px at ${x}px ${y}px)`],
      },
      {
        duration: 350, // Reduced from 500ms to 350ms for faster animations
        easing: 'ease-in-out', // Smooth ease-in-out animation
        pseudoElement: '::view-transition-new(root)', // Apply the animation to the new root element
      },
    );
  } catch (error) {
    console.error('Error in toggleDarkMode:', error);
    // skip the animation and just update the DOM
    if (transition) transition.skipTransition();
  }
};
